import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  CircularProgress,
  Box,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useTranslation, Trans } from 'react-i18next';

interface FolderInfo {
  Name: string;
  IsLoadedApp?: boolean;
  LoadedSubs?: number;
  IsLoadedSub?: boolean;
}

interface RemoveFromDbDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  folders: FolderInfo[];
  isLoading?: boolean;
}

/**
 * RemoveFromDbDialog
 *
 * Confirmation dialog before removing one or more folders from the database.
 * - Shows the folder name(s) and sequence count (if applicable)
 * - Informs user that they can re-process to restore the entries
 * - Requires user confirmation
 */
export function RemoveFromDbDialog({
  open,
  onClose,
  onConfirm,
  folders,
  isLoading = false,
}: RemoveFromDbDialogProps) {
  const { t } = useTranslation('load');
  const isSingle = folders.length === 1;
  const folder = isSingle ? folders[0] : null;
  const sequenceCount = folder?.IsLoadedApp ? folder.LoadedSubs ?? 0 : 0;
  const isApplication = folder?.IsLoadedApp;
  const totalSequences = folders.reduce(
    (sum, f) => sum + (f.IsLoadedApp ? f.LoadedSubs ?? 0 : 0),
    0,
  );

  return (
    <Dialog
      data-testid="remove-from-db-dialog"
      open={open && folders.length > 0}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle data-testid="remove-from-db-dialog-title">
        {t('removeFromDb.title')}
      </DialogTitle>

      <DialogContent sx={{ py: 2 }} data-testid="remove-from-db-dialog-content">
        <Stack spacing={2}>
          {/* Warning icon and message */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <WarningIcon sx={{ color: 'warning.main', mt: 0.25 }} />
            <Stack spacing={1}>
              {isSingle ? (
                <>
                  <Typography variant="body1" data-testid="remove-from-db-dialog-message">
                    <Trans
                      i18nKey="removeFromDb.messageSingle"
                      values={{ name: folder?.Name }}
                      components={{ strong: <strong /> }}
                      ns="load"
                    />
                  </Typography>
                  {isApplication && sequenceCount > 0 && (
                    <Typography variant="body2" color="text.secondary" data-testid="remove-from-db-dialog-sequence-count">
                      <Trans
                        i18nKey="removeFromDb.sequenceCount"
                        values={{ count: sequenceCount }}
                        components={{ strong: <strong /> }}
                        ns="load"
                      />
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  <Typography variant="body1" data-testid="remove-from-db-dialog-message">
                    <Trans
                      i18nKey="removeFromDb.messageMultiple"
                      values={{ count: folders.length }}
                      components={{ strong: <strong /> }}
                      ns="load"
                    />
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {folders.map((f) => (
                      <Typography component="li" variant="body2" key={f.Name} color="text.secondary">
                        {f.Name}
                        {f.IsLoadedApp && f.LoadedSubs ? ` ${t('removeFromDb.sequenceLabel', { count: f.LoadedSubs })}` : ''}
                      </Typography>
                    ))}
                  </Box>
                  {totalSequences > 0 && (
                    <Typography variant="body2" color="text.secondary" data-testid="remove-from-db-dialog-sequence-count">
                      <Trans
                        i18nKey="removeFromDb.totalSequences"
                        values={{ count: totalSequences }}
                        components={{ strong: <strong /> }}
                        ns="load"
                      />
                    </Typography>
                  )}
                </>
              )}
            </Stack>
          </Box>

          {/* Info box about restoring */}
          <Typography
            variant="body2"
            sx={{
              p: 1.5,
              backgroundColor: '#e3f2fd',
              color: '#1565c0',
              borderRadius: 1,
              border: '1px solid #90caf9',
            }}
          >
            <Trans
              i18nKey={isSingle ? 'removeFromDb.restoreInfoSingle' : 'removeFromDb.restoreInfoMultiple'}
              components={{ strong: <strong /> }}
              ns="load"
            />
          </Typography>

          {/* Confirmation text */}
          <Typography variant="body2" color="text.secondary">
            {t('removeFromDb.confirmContinue')}
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          data-testid="remove-from-db-dialog-cancel-button"
          onClick={onClose}
          disabled={isLoading}
          variant="outlined"
        >
          {t('removeFromDb.cancel')}
        </Button>
        <Button
          data-testid="remove-from-db-dialog-confirm-button"
          onClick={async () => {
            await onConfirm();
            onClose();
          }}
          disabled={isLoading}
          color="error"
          variant="contained"
        >
          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" data-testid="remove-from-db-dialog-loading-spinner" />
              {t('removeFromDb.removing')}
            </Box>
          ) : (
            t('removeFromDb.removeButton')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
